import type { NoteColor } from "./theme";
import type { RichDoc, RichNode } from "./tiptap";

/**
 * What shape a note has.
 *
 * `plain` is every note that existed before templates. `recipe` and `fridge`
 * unlock structured panels backed by real tables; the rest differ only in the
 * document they start life with, but keep their type so the list can filter
 * and badge them.
 */
export const NOTE_TYPES = ["plain", "recipe", "fridge", "daily", "meeting", "shopping"] as const;
export type NoteType = (typeof NOTE_TYPES)[number];

export type NoteTemplate = {
  id: NoteType;
  label: string;
  description: string;
  /**
   * A lucide icon name. `core` is framework-agnostic, so the web app maps this
   * to a component rather than importing one here.
   */
  icon: string;
  color: NoteColor;
  /** The title the note opens with. Takes the clock so daily notes date themselves. */
  title: (now: Date) => string;
  content: RichDoc;
};

/* ------------------------------------------------- document seed builders */

const text = (value: string): RichNode => ({ type: "text", text: value });
const p = (value?: string): RichNode =>
  value ? { type: "paragraph", content: [text(value)] } : { type: "paragraph" };
const h = (level: 1 | 2 | 3, value: string): RichNode => ({
  type: "heading",
  attrs: { level },
  content: [text(value)],
});
const bullets = (...values: string[]): RichNode => ({
  type: "bulletList",
  content: values.map((v) => ({ type: "listItem", content: [p(v)] })),
});
const steps = (count: number): RichNode => ({
  type: "orderedList",
  attrs: { start: 1 },
  content: Array.from({ length: count }, () => ({ type: "listItem", content: [p()] })),
});
const checks = (count: number): RichNode => ({
  type: "taskList",
  content: Array.from({ length: count }, () => ({
    type: "taskItem",
    attrs: { checked: false },
    content: [p()],
  })),
});
const doc = (...content: RichNode[]): RichDoc => ({ type: "doc", content });

const dayTitle = (now: Date) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(now);

/* ------------------------------------------------------------- the registry */

/** Blank stays first: the fastest path is still an empty note. */
export const NOTE_TEMPLATES: readonly NoteTemplate[] = [
  {
    id: "plain",
    label: "Blank note",
    description: "An empty page.",
    icon: "FileText",
    color: "sand",
    title: () => "",
    content: doc(),
  },
  {
    id: "recipe",
    label: "Recipe",
    description: "Ingredients checked against your fridge, plus the method.",
    icon: "ChefHat",
    color: "coral",
    title: () => "",
    content: doc(h(2, "Method"), steps(3), h(2, "Notes"), p()),
  },
  {
    id: "fridge",
    label: "Fridge",
    description: "What you have in, with quantities and expiry dates.",
    icon: "Refrigerator",
    color: "mint",
    title: () => "Fridge",
    content: doc(p("Everything below is shared with every fridge note and with your recipes.")),
  },
  {
    id: "daily",
    label: "Daily note",
    description: "Today, with room for highlights, tasks and loose thoughts.",
    icon: "CalendarDays",
    color: "sky",
    title: dayTitle,
    content: doc(h(2, "Highlights"), p(), h(2, "Tasks"), checks(3), h(2, "Notes"), p()),
  },
  {
    id: "meeting",
    label: "Meeting notes",
    description: "Attendees, agenda, decisions and what happens next.",
    icon: "Users",
    color: "lilac",
    title: () => "",
    content: doc(
      h(2, "Attendees"),
      p(),
      h(2, "Agenda"),
      bullets("", "", ""),
      h(2, "Decisions"),
      p(),
      h(2, "Action items"),
      checks(3),
    ),
  },
  {
    id: "shopping",
    label: "Shopping list",
    description: "A checklist for the shop.",
    icon: "ShoppingCart",
    color: "amber",
    title: () => "Shopping list",
    content: doc(checks(5)),
  },
];

export function noteTemplate(id: NoteType): NoteTemplate {
  return NOTE_TEMPLATES.find((t) => t.id === id) ?? NOTE_TEMPLATES[0]!;
}
